import CustomLoader from "@/components/CustomLoader/customLoader.component";
import { useMediaQuery } from "@/hooks/AppContext/mediaQuery";
import { addFavoriteSearch, deleteFavoriteSearch } from "@/models/tasks.model";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shadcn/components/ui/dialog";
import { Input } from "@/shadcn/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils";
import { ChevronsUpDown, Star } from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

type FavoriteSearchDialogProps = {};

const FavoriteSearchDialog: React.FC<FavoriteSearchDialogProps> = () => {
  const { favoriteSearches, initialGetUserFavoriteSearches } =
    useContext(AppContext);
  const [isFavorite, setIsFavorite] = useState<boolean>(true);
  const [favoriteID, setFavoriteID] = useState<number | null>(null);
  const [favoriteName, setFavoriteName] = useState<string | null>(null);
  const [input, setInput] = useState<string | null>(null);
  const [creationState, setCreationState] = useState<
    "creation" | "submitting" | "success" | "failed"
  >("creation");
  const [isOpen, setIsOpen] = useState(false);
  const params = useSearchParams();
  const nav = useNavigate();
  const isNotSmall = useMediaQuery("sm");

  useEffect(() => {
    if (favoriteSearches.length === 0) {
      setIsFavorite(false);
      return;
    }

    if (favoriteSearches.length > 0) {
      const urlQuery = location.search;

      const isFavorite = favoriteSearches.some((search) => {
        return search.search === urlQuery;
      });
      setFavoriteID(
        favoriteSearches.find((search) => search.search === urlQuery)?.id ??
          null
      );
      setFavoriteName(
        favoriteSearches.find((search) => search.search === urlQuery)?.name ??
          null
      );
      setIsFavorite(isFavorite);
    }
  }, [isFavorite, favoriteSearches, params]);

  async function handleSubmitNewFavorite() {
    if (!input || !location.search) {
      return;
    }

    setCreationState("submitting");
    const response = await addFavoriteSearch({
      name: input,
      search: location.search,
    });

    if (response.rStatus === "error") {
      setCreationState("failed");
      genericFailureToast("Failed to add favorite search");
      return;
    }

    setCreationState("success");
    genericSuccessToast("Favorite search added");
    initialGetUserFavoriteSearches({ force: true });
    closeDialog();
  }

  async function handleDeleteFavorite() {
    if (!favoriteID) {
      return;
    }
    setCreationState("submitting");
    const response = await deleteFavoriteSearch(favoriteID);

    if (response.rStatus === "error") {
      setCreationState("failed");
      genericFailureToast("Failed to delete favorite search");
      return;
    }

    setCreationState("success");
    genericSuccessToast("Favorite search deleted");
    initialGetUserFavoriteSearches({ force: true });
    closeDialog();
  }

  function closeDialog() {
    setIsOpen(false);
    setInput(null);
    setCreationState("creation");
  }

  return (
    <Dialog modal open={isOpen} onOpenChange={setIsOpen}>
      {isNotSmall ? (
        favoriteName && (
          <div className="px-2 font-bold cursor-default">{favoriteName}</div>
        )
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="flex items-center gap-2 w-full justify-between"
            >
              <span>{favoriteName || "Favorites"}</span>
              <ChevronsUpDown className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <Command>
              <CommandInput placeholder="Search favorites" />
              <CommandList>
                <ScrollArea className="h-[200px]">
                  <CommandEmpty>No favorites found.</CommandEmpty>
                  <CommandGroup>
                    {favoriteSearches.map((favorite) => (
                      <CommandItem
                        key={favorite.id}
                        onSelect={() => {
                          nav(favorite.search);
                          closeDialog();
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <span>{favorite.name}</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </ScrollArea>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      <DialogTrigger asChild>
        <Star
          className={`ml-1 h-4 w-4 shrink-0 hover:stroke-yellow-300 ${
            isFavorite && "fill-yellow-300 stroke-yellow-300"
          } `}
        />
      </DialogTrigger>
      {isFavorite ? (
        // Remove favorite
        <DialogContent className="sm:max-w-[425px] !rounded-sm">
          <DialogHeader>
            <DialogTitle>Remove favorite?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete this
              query.
            </DialogDescription>
          </DialogHeader>
          {creationState === "creation" ? (
            <></>
          ) : creationState === "submitting" ? (
            <CustomLoader size="sm" />
          ) : (
            <DialogDescription>Failed</DialogDescription>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={creationState === "submitting"}
              onClick={() => {
                handleDeleteFavorite();
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      ) : (
        // Add favorite
        <DialogContent className="sm:max-w-[425px] !rounded-sm">
          <DialogHeader>
            <DialogTitle>Add favorite?</DialogTitle>
            <DialogDescription>
              This action will save this query to your favorite searches.
            </DialogDescription>
          </DialogHeader>
          {creationState === "creation" || creationState === "failed" ? (
            <>
              <div className="grid grid-cols-[auto_1fr]">
                <label
                  className="flex items-center create-favorite font-light text-center mr-3"
                  htmlFor="create-favorite"
                >
                  Name
                </label>
                <Input
                  id="create-favorite"
                  value={input ? input : ""}
                  placeholder={`Search name`}
                  onChange={(e) => setInput(e.target.value)}
                  maxLength={255}
                />
              </div>
              {creationState === "failed" && (
                <div className="text-red-600">Failed to add to favorites</div>
              )}
            </>
          ) : (
            creationState === "submitting" && <CustomLoader size="sm" />
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={async () => {
                await handleSubmitNewFavorite();
              }}
              disabled={
                input === null ||
                input === "" ||
                input.length < 3 ||
                creationState === "submitting"
              }
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default FavoriteSearchDialog;
